<script lang="ts">import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { CheckIcon, RemoveIcon } from "@infinitro/icons";
import { Input } from "@infinitro/input";
import { onDestroy, onMount } from "svelte";
import { emailChecksStarted, invalidateIdentifierResolution, startEmailVerify, stateEmail, stateEmailVerified, stateFormMode, stateIdentifierResolved, stateNeedSecondIdentifier, submit } from "../state";
import { FormMode } from "../types";
import { isValidEmail } from "../utils/passwordCredentials";
let element = null;
let otpConfirmed = false;
let valid = false;
let touched = false;
export let skipVerify = false;
export let required = true;
let emailAddress;
let blurredEmails = [];
let verifyingEmail = false;
let autofillLocked = true;
const sleep = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));
const unlockAutofill = () => {
	autofillLocked = false;
};
const validateEmail = (email) => {
	return skipVerify ? isValidEmail(email) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
const onInput = (value) => {
	const email = value ?? element?.value ?? "";
	valid = validateEmail(email);
	if (skipVerify) {
		touched = true;
		stateEmail.set(email);
		return;
	}
	if (valid) {
		stateEmail.set(email);
	}
};
const onBlur = async (event) => {
	const email = element?.value ?? "";
	valid = validateEmail(email);
	if (skipVerify) {
		if (email) touched = true;
		stateEmail.set(email);
		return;
	}
	if (valid) startEmailVerify(event, true);
};
stateEmail.subscribe((_value) => {
	emailAddress = _value ?? "";
	if (element) {
		element.value = _value;
		valid = validateEmail(_value ?? "");
	}
	if (_value === "") {
		otpConfirmed = false;
		valid = false;
	}
});
stateEmailVerified.subscribe((value) => {
	otpConfirmed = value;
});
// In the password-credentials flow there's no OTP step, so "confirmed" (locked, with a
// remove icon) tracks identifier resolution instead of email verification.
// stateIdentifierResolved is also set when PreLogin redirects an unknown identifier to
// signup, or when a second identifier is still needed - neither means THIS field was
// resolved, so only lock it outside those cases (plain login identifier resolution).
$: confirmed = skipVerify ? $stateIdentifierResolved && $stateFormMode !== FormMode.Register && !$stateNeedSecondIdentifier : otpConfirmed;
const onRemove = (event) => {
	event?.preventDefault();
	event?.stopPropagation();
	if (skipVerify) {
		// Clearing the locked identifier goes back to the initial login state.
		invalidateIdentifierResolution();
	} else {
		stateEmailVerified.set(false);
	}
	stateEmail.set("");
	// Removing unlocks the field for typing right away - without this it stays readonly
	// (autofill protection) until the player clicks into it a second time.
	autofillLocked = false;
	element?.focus();
};
const handleVerifyClick = async (event) => {
	event?.preventDefault();
	event?.stopPropagation();
	startEmailVerify(event, false);
};
stateFormMode.subscribe(async (value) => {
	if (element && !confirmed) {
		await sleep(100);
		element?.focus();
	}
});
const onFieldKeydown = (event) => {
	if (!skipVerify || event.key !== "Enter" || event.shiftKey) return;
	event.preventDefault();
	submit(event);
};
function handleKeyDown(event) {
	if (!skipVerify && event.key === "Enter" && !event.shiftKey && !verifyingEmail && valid && !emailChecksStarted.includes($stateEmail)) {
		event.preventDefault();
		handleVerifyClick();
	}
}
$: useAlternative4 = $stateUi.emailVerify.emailVerifyStyle === "alternative4";
$: useExpandedRankPresentation = $stateUi.layout?.useExpandedRankPresentation ?? false;
$: useFlat = $stateUi.modal?.modalStyle === "flat";
$: flatVerifyButtonStyle = useFlat ? `height: 48px; width: 71px; border-radius: 8px; background: var(--color-primary-500); opacity: ${valid ? 1 : .5};` : useAlternative4 ? "" : "height: 48px";
onMount(async () => {
	if (element && emailAddress) {
		element.value = emailAddress;
		valid = validateEmail(emailAddress);
	}
	if (element && !confirmed) {
		await sleep(100);
		element?.focus();
	}
	window.addEventListener("keydown", handleKeyDown);
});
onDestroy(() => {
	window.removeEventListener("keydown", handleKeyDown);
});
</script>

<span class="section" class:section-flat={useFlat} class:section-labeled={skipVerify}>
	{#if skipVerify}
		<label class="label" for={skipVerify ? 'registerEmail' : 'email'}>{$t(Verbiage.email)} *</label>
	{/if}
	<div class="input" class:input-fixed={!skipVerify}>
		<Input
			type="email"
			name={skipVerify ? 'register-email' : 'email'}
			id={skipVerify ? 'registerEmail' : 'email'}
			autocomplete={skipVerify ? 'off' : 'email'}
			readonly={skipVerify && autofillLocked}
			onFocus={skipVerify ? unlockAutofill : undefined}
			placeholder={skipVerify
				? $t(Verbiage.enter_your_email_address)
				: `${$t(Verbiage.enter_your_email_address)}` +
					(required ? `` : ` (${$t(Verbiage.optional)})`)}
			{required}
			bind:element
			{onInput}
			{onBlur}
			onKeydown={onFieldKeydown}
			disabled={confirmed}
			customBorderRadius={useFlat ? '8px' : undefined}
			inputClasses={useFlat ? 'auth-flat-input-el' : ''}
			error={skipVerify && touched && !valid}
			message={skipVerify && touched && !valid ? $t(Verbiage.email_address_invalid) : ''}
		/>
		{#if confirmed}<span class="trailing-icon"
				><button type="button" class="remove" on:click={onRemove}><RemoveIcon /></button><span
					class="check"><CheckIcon /></span
				></span
			>{/if}
	</div>
	{#if !confirmed && !skipVerify}
		<div class="verify-container">
			<Button
				className={valid && useExpandedRankPresentation ? 'enable-verify-alternative' : ''}
				variant={useFlat ? 'accent' : useAlternative4 ? 'tertiary' : 'secondary'}
				text={$t(Verbiage.verify)}
				onClick={handleVerifyClick}
				disabled={!valid}
				styleCustom={flatVerifyButtonStyle}
				id="verifyButton"
			/>
		</div>
	{/if}
</span>

<style>
	.section {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
}

	.section-flat {
		width: 100%;
		gap: 10px;
		padding-top: 0px;
		padding-bottom: 0px;
}

	.label {
		margin-top: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.025em;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.section-labeled {
		flex-direction: column;
		align-items: stretch;
}

	div.input {
		position: relative;
		display: flex;
		width: 100%;
		align-items: center;
		white-space: nowrap;
}

	div.input-fixed {
		height: 48px;
}

	div.verify-container {
		position: relative;
}

	:global(.auth-flat-input) {
		background: var(--color-background-500) !important;
		border: 0 !important;
		height: 48px !important;
	}

	:global(.auth-flat-input::before),
	:global(.auth-flat-input::after) {
		content: none !important;
	}

	.trailing-icon {
		pointer-events: auto;
		position: absolute;
		top: 4px;
		right: 0px;
		display: flex;
		padding-right: 0.5rem;
}

	.check {
		position: absolute;
		top: 14px;
}

	.remove {
		cursor: pointer;
}

	input,
	input:disabled {
		display: block;
		height: 48px;
		width: 100%;
		border-radius: 16px;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		padding-left: 1rem;
		padding-right: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		background: var(--gradient-alternative-4);
}

	input:focus {
		display: block;
		width: 100%;
		border-radius: 16px;
		border-width: 1px;
		background-color: var(--color-background-100);
		padding: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	input:hover {
		background: var(--gradient-main);
}

	input:active {
		background: var(--gradient-main);
}

	input:focus-within {
		border-color: var(--color-secondary-500);
}

	.input-alternative:hover,
	.input-alternative:active,
	.input-alternative:focus,
	.input-alternative {
		background-image: unset;
	}

	input:-webkit-autofill {
		--tw-text-opacity: 1 !important;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1)) !important;
		background: var(--gradient-main) !important;
		background-color: transparent !important;
}

	input:-webkit-autofill,
	input:autofill,
	input:-internal-autofill-selected {
		--tw-text-opacity: 1 !important;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1)) !important;
		background: var(--gradient-main) !important;
		background-color: transparent !important;
}

	input:-webkit-autofill:focus {
		border-color: var(--color-secondary-500);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		background: var(--gradient-main);
}

	input:-webkit-autofill:focus,
	input:autofill:focus {
		border-color: var(--color-secondary-500);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		background: var(--gradient-main);
}
</style>
